<script setup lang="ts">
import { computed } from 'vue';
import { NuxtLink } from '#components';
import { buttonVariants } from '~/components/ui/button';
import { ArrowRight, Plus } from 'lucide-vue-next';
import { cn } from '@@/shared/utils';
import Button from '../ui/button/Button.vue';

export interface ButtonProps {
	id: string;
	label?: string | null;
	variant?: string | null;
	url?: string | null;
	type?: 'page' | 'post' | 'url' | 'submit' | 'page-block' | null;
	page?: { permalink: string | null };
	post?: { slug: string | null };
	page_block?: { id: string | number } | string | null;
	size?: 'default' | 'sm' | 'lg' | 'icon';
	icon?: 'arrow' | 'plus';
	customIcon?: any;
	iconPosition?: 'left' | 'right';
	className?: string;
	disabled?: boolean;
	block?: boolean;
	target?: '_blank' | '_self' | '_parent' | '_top';
}

const props = withDefaults(defineProps<ButtonProps>(), {
	size: 'default',
	iconPosition: 'left',
	disabled: false,
	block: false,
});

const icons: Record<string, any> = {
	arrow: ArrowRight,
	plus: Plus,
};

const Icon = computed(() => props.customIcon || (props.icon ? icons[props.icon] : null));

const href = computed(() => {
	if (props.type === 'page' && props.page?.permalink) return props.page.permalink;
	if (props.type === 'post' && props.post?.slug) return `/blog/${props.post.slug}`;
	if (props.type === 'page-block') {
		// For page-block type, we'll handle it with a click handler instead of href
		return undefined;
	}
	return props.url || undefined;
});

const handlePageBlockClick = (e: Event) => {
	if (props.type !== 'page-block' || !props.page_block) return;
	
	e.preventDefault();
	
	// Get the block ID - handle both object and string formats
	const blockId = typeof props.page_block === 'string' 
		? props.page_block 
		: props.page_block?.id;
	
	if (!blockId) return;
	
	// Find the target block element
	const targetElement = document.getElementById(`block-${blockId}`);
	
	if (targetElement) {
		// Get the navigation header to calculate its height
		const navHeader = document.querySelector('header[class*="sticky"], header[class*="absolute"]');
		const navHeight = navHeader ? navHeader.getBoundingClientRect().height : 0;
		
		// Additional offset for block padding - larger on desktop (>= 768px)
		const isDesktop = window.innerWidth >= 768;
		const paddingOffset = isDesktop ? 48 : 32;
		
		// Calculate the target position, accounting for the navigation height and padding
		const elementPosition = targetElement.getBoundingClientRect().top;
		const offsetPosition = elementPosition + window.pageYOffset - navHeight - paddingOffset;
		
		// Smooth scroll to the block with offset for navigation and padding
		window.scrollTo({
			top: offsetPosition,
			behavior: 'smooth'
		});
	}
};

const buttonClasses = computed(() =>
	cn(
		buttonVariants({ variant: props.variant as any, size: props.size }),
		props.className,
		props.disabled && 'opacity-50 cursor-not-allowed',
		props.block && 'w-full',
	),
);

const linkComponent = computed(() => {
	if (props.type === 'page-block') {
		return 'button';
	}
	return href.value ? NuxtLink : 'button';
});

const handleClick = (e: Event) => {
	if (props.type === 'page-block') {
		handlePageBlockClick(e);
	}
};
</script>
<template>
	<Button
		:variant="variant as any"
		:size="size"
		:class="buttonClasses"
		:disabled="disabled"
		:as="linkComponent"
		:href="href"
		:target="target"
		@click="handleClick"
		v-bind="$attrs"
	>
		<span class="flex items-center space-x-2">
			<component :is="Icon" v-if="Icon && iconPosition === 'left'" class="size-4 shrink-0" />
			<span v-if="label" class="uppercase">{{ label }}</span>
			<slot v-if="$slots.default" />
			<component :is="Icon" v-if="Icon && iconPosition === 'right'" class="size-4 shrink-0" />
		</span>
	</Button>
</template>
